/**
 * Wishlist UI — heart button, counter, favorites page; backed by window.AicoWishlist
 */
(() => {
  const shopRoot = () => {
    const root =
      window.PDT_SHOP_ROOT ||
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  // Locale-aware storefront URL (products, cart, etc.)
  const localeUrl = (path) => {
    const normalized = path.startsWith('/') ? path.slice(1) : path;
    return `${shopRoot()}${normalized}`;
  };

  const escapeHtml = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (m) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m])
    );

  const texts = () =>
    Object.assign(
      {
        empty: 'Your favorites list is empty.',
        remove: 'Remove',
        add: 'ADD',
        add_to_favorites: 'Add to favorites',
        remove_from_favorites: 'Remove from favorites',
        signed_out_hint: '',
        adding: 'Adding…',
        added: 'Added',
        limit_reached: 'Limit reached',
        add_error: 'Error',
        add_failed: 'Could not add to cart',
      },
      window.PDT_FAV_TEXTS || {}
    );

  const productUrl = (handle, variantId) => {
    if (!handle) return '#';
    const base = localeUrl(`products/${encodeURIComponent(handle)}`);
    return variantId ? `${base}?variant=${encodeURIComponent(variantId)}` : base;
  };

  // Title in the current storefront locale, fallback to the stored productTitle
  const localeKey = () => String(window.PDT_LOCALE || 'de').toLowerCase().split('-')[0];
  const localizedTitle = (item) => {
    const tt = item.productTitleTranslations;
    return (tt && tt[localeKey()]) || item.productTitle || '';
  };

  // --- Heart button next to a product -----------------------------------

  class FavoriteButton extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('button');
      if (!this.btn) return;
      this.update();
      this.btn.addEventListener('click', this.onClick);
      window.addEventListener('wishlist:change', this.update);
    }
    disconnectedCallback() {
      window.removeEventListener('wishlist:change', this.update);
    }
    item() {
      const d = this.dataset;
      let productTitleTranslations = null;
      if (d.productTitleTranslations) {
        try {
          productTitleTranslations = JSON.parse(d.productTitleTranslations);
        } catch (e) {
          productTitleTranslations = null;
        }
      }
      return {
        productId: d.productId,
        variantId: d.variantId,
        productTitle: d.productTitle,
        productTitleTranslations: productTitleTranslations,
        price: d.price,
        variantTitle: d.variantTitle,
        compareAtPrice: d.compareAtPrice,
        imageUrl: d.image,
        productHandle: d.productHandle,
        sku: d.sku,
        available: d.available !== 'false',
      };
    }
    onClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!window.AicoWishlist || !this.dataset.variantId) return;
      window.AicoWishlist.toggle(this.item()).catch(() => {});
      this.update();
    };
    update = () => {
      if (!this.btn) return;
      const active = !!(window.AicoWishlist && window.AicoWishlist.has(this.dataset.variantId));
      this.classList.toggle('is-active', active);
      this.btn.setAttribute('aria-pressed', String(active));
      const t = texts();
      const label = active ? t.remove_from_favorites : t.add_to_favorites;
      this.btn.setAttribute('aria-label', label);
      this.btn.setAttribute('title', label);
    };
  }
  if (!customElements.get('favorite-button')) {
    customElements.define('favorite-button', FavoriteButton);
  }

  // --- Header badge count -----------------------------------------------

  class FavoritesCounter extends HTMLElement {
    connectedCallback() {
      this.update();
      window.addEventListener('wishlist:change', this.update);
    }
    disconnectedCallback() {
      window.removeEventListener('wishlist:change', this.update);
    }
    update = () => {
      const count = window.AicoWishlist ? window.AicoWishlist.count() : 0;
      this.textContent = count > 0 ? String(count) : '';
      this.classList.toggle('is-empty', count === 0);
    };
  }
  if (!customElements.get('favorites-counter')) {
    customElements.define('favorites-counter', FavoritesCounter);
  }

  // --- Favorites page list ----------------------------------------------

  async function handleFavAddToCart(btn) {
    if (btn.disabled) return;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    const card = btn.closest('[data-pp-reco-card]');
    const productId = (card && card.getAttribute('data-product-id')) || '';
    const t = texts();
    const original = btn.textContent.trim();
    btn.disabled = true;
    btn.classList.add('is-loading');
    btn.textContent = t.adding;
    try {
      const res = await fetch(localeUrl('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      if (!res.ok) {
        if (res.status === 422) {
          btn.textContent = t.limit_reached;
          setTimeout(() => { btn.textContent = original; btn.disabled = false; btn.classList.remove('is-loading'); }, 2000);
          return;
        }
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || t.add_failed);
      }
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            sourceId: 'pp-fav',
            data: { source: 'product-form-component', itemCount: 1, productId: productId },
          },
        })
      );
      btn.textContent = t.added || '✓';
      setTimeout(() => {
        btn.textContent = original;
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }, 1200);
    } catch (err) {
      console.error('[wishlist] add to cart failed:', err);
      btn.textContent = t.add_error;
      setTimeout(() => {
        btn.textContent = original;
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }, 1600);
    }
  }

  // Simplified aico product card: image, title, variant name, price, add to cart
  const renderFavCard = (item, t) => {
    const url = escapeHtml(productUrl(item.productHandle, item.variantId));
    const title = escapeHtml(localizedTitle(item));
    // Loose (offen) variants show the configured label (window.AICO_OFFEN - theme.liquid)
    const offenLabel = (window.AICO_OFFEN && window.AICO_OFFEN.label) || '';
    let rawVTitle = item.variantTitle || '';
    if (offenLabel && rawVTitle.toLowerCase().indexOf('offen') !== -1) {
      rawVTitle = offenLabel;
    }
    const vTitle = rawVTitle && rawVTitle.toLowerCase() !== 'default title' ? escapeHtml(rawVTitle) : '';
    const hasCompare = !!item.compareAtPrice;

    return `
      <li class="aico-pc" data-variant-id="${escapeHtml(item.variantId)}" data-product-id="${escapeHtml(item.productId)}">
        <div class="aico-pc__container">
          <button type="button" class="aico-fav__remove" data-fav-remove data-variant-id="${escapeHtml(item.variantId)}" aria-label="${escapeHtml(t.remove)}">
            <svg viewBox="0 0 20 20" aria-hidden="true" fill="none"><path d="M5 5 L15 15 M15 5 L5 15" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
          </button>
          <div class="aico-pc__wrapper">
            <a href="${url}" class="aico-pc__link" aria-label="${title}">
              <span class="aico-pc__image-container">
                <span class="aico-pc__image-wrapper">
                  ${item.imageUrl ? `<img class="aico-pc__image" src="${escapeHtml(item.imageUrl)}" alt="${title}" loading="lazy">` : ''}
                </span>
              </span>
            </a>
            <div class="aico-pc__details">
              <a class="aico-pc__title" href="${url}">${title}</a>
              ${vTitle ? `<div class="aico-pc__variant-name">${vTitle}</div>` : ''}
            </div>
            <div class="aico-pc__inner">
              <div class="aico-pc__bottom">
                <div class="aico-pc__price">
                  <span class="aico-pc__normal-price${hasCompare ? ' aico-pc__sale-price' : ''}">${escapeHtml(item.price)}</span>
                  ${hasCompare ? `<span class="aico-pc__compare-at-price">${escapeHtml(item.compareAtPrice)}</span>` : ''}
                </div>
                <div class="aico-pc__add-to-cart-wrapper">
                  <button type="button" class="aico-pc__add-to-cart" data-pp-reco-add data-variant-id="${escapeHtml(item.variantId)}" ${item.available ? '' : 'disabled'}>${escapeHtml(t.add)}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>
    `;
  };

  class FavoritesList extends HTMLElement {
    connectedCallback() {
      this.boundClick = this.onClick.bind(this);
      this.boundRender = this.render.bind(this);
      this.addEventListener('click', this.boundClick);
      this.render();
      window.addEventListener('wishlist:change', this.boundRender);
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.boundClick);
      window.removeEventListener('wishlist:change', this.boundRender);
    }
    showEmpty() {
      const msg = this.dataset.emptyMessage || texts().empty;
      this.innerHTML = `<div class="aico-fav__empty">${escapeHtml(msg)}</div>`;
    }
    onClick(e) {
      const removeBtn = e.target.closest('[data-fav-remove]');
      if (removeBtn && this.contains(removeBtn)) {
        e.preventDefault();
        if (window.AicoWishlist) window.AicoWishlist.remove(removeBtn.dataset.variantId).catch(() => {});
        return;
      }
      const addBtn = e.target.closest('[data-pp-reco-add]');
      if (addBtn && this.contains(addBtn)) {
        e.preventDefault();
        handleFavAddToCart(addBtn);
      }
    }
    render() {
      const t = texts();
      const items = window.AicoWishlist ? window.AicoWishlist.getItems() : [];
      if (!items.length) {
        this.showEmpty();
        return;
      }
      this.innerHTML = `<ul class="aico-fav__grid">${items.map((i) => renderFavCard(i, t)).join('')}</ul>`;
    }
  }
  if (!customElements.get('favorites-list')) {
    customElements.define('favorites-list', FavoritesList);
  }
})();
